import { request } from '@umijs/max';

// User APIs
export async function currentUser(options?: { [key: string]: any }) {
  return request<API.CurrentUser>('/api/current-user', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function login(body: API.LoginParams, options?: { [key: string]: any }) {
  return request<API.LoginResult>('/api/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function logout(options?: { [key: string]: any }) {
  return request('/api/logout', {
    method: 'POST',
    ...(options || {}),
  });
}

// Call APIs
export async function getActiveCalls(options?: { [key: string]: any }) {
  return request<{ calls: API.Call[] }>('/api/calls/active', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getCallLogs(params?: any, options?: { [key: string]: any }) {
  return request<{ logs: API.CallLog[]; total: number }>('/api/calls/logs', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function getCallTrendStats(options?: { [key: string]: any }) {
  return request<any>('/api/calls/stats/trend', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getDailyStats(params?: any, options?: { [key: string]: any }) {
  return request<API.DailyStats>('/api/calls/stats/daily', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function getCsrMetricCalls(params: {
  csr_id: number;
  metric: string;
  start_date?: string;
  end_date?: string;
}, options?: { [key: string]: any }) {
  return request<{
    metric: string;
    label: string;
    csrId: number;
    csrName: string;
    items: {
      callId: number;
      callSid?: string;
      phone: string;
      customerName?: string | null;
      time?: string;
      direction?: string;
      phase?: string;
      result?: string;
      eventId?: number;
    }[];
    dateRange?: { start: string; end: string };
  }>('/api/calls/stats/daily/csr-calls', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function getCallDetails(callSid: string, options?: { [key: string]: any }) {
  return request<any>(`/api/calls/detail`, {
    method: 'GET',
    params: { call_sid: callSid },
    ...(options || {}),
  });
}

// CSR APIs
export async function getCSRAgents(options?: { [key: string]: any }) {
  return request<{ agents: API.CSRAgent[] }>('/api/csr/agents', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getCSRState(options?: { [key: string]: any }) {
  return request<API.CSRState>('/api/csr/state', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function updateCSRStatus(
  agentId: number,
  status: string,
  options?: { [key: string]: any },
) {
  return request(`/api/csr/agents/${agentId}/status`, {
    method: 'PUT',
    data: { status },
    ...(options || {}),
  });
}

// Contact APIs
export async function getContacts(params?: any, options?: { [key: string]: any }) {
  return request<{ contacts: API.Contact[]; total: number }>('/api/contacts', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function getContactByPhone(phone: string, options?: { [key: string]: any }) {
  return request<API.Contact>(`/api/contacts/${phone}`, {
    method: 'GET',
    ...(options || {}),
  });
}

// Avatar/Virtual Office APIs
export async function getAvatarState(options?: { [key: string]: any }) {
  return request<API.AvatarState>('/avatars/api/state', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function updateAvatarPosition(
  agentId: number,
  position: { x: number; y: number; z: number },
  options?: { [key: string]: any },
) {
  return request('/avatars/api/move', {
    method: 'POST',
    data: { agentId, ...position },
    ...(options || {}),
  });
}

// Twilio APIs
export async function getTwilioToken(identity: string, options?: { [key: string]: any }) {
  return request<API.TwilioToken>('/api/twilio/token', {
    method: 'POST',
    data: { identity },
    ...(options || {}),
  });
}

// Recording APIs
export async function getRecordings(params?: any, options?: { [key: string]: any }) {
  return request<{ recordings: API.Recording[]; total: number }>('/api/recordings', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

// AI Agent Configuration APIs
export async function getSystemPrompt(options?: { [key: string]: any }) {
  return request<{ prompt: string }>('/api/system-prompt', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function saveSystemPrompt(prompt: string, options?: { [key: string]: any }) {
  return request('/api/system-prompt', {
    method: 'POST',
    data: { prompt },
    ...(options || {}),
  });
}

export async function buildWorkflow(userInput: string, options?: { [key: string]: any }) {
  return request<{ aiResponse: string; action: string; [key: string]: any }>('/api/build-workflow', {
    method: 'POST',
    data: { userInput },
    ...(options || {}),
  });
}

export async function confirmWorkflow(proposal: any, options?: { [key: string]: any }) {
  return request<{ message: string }>('/api/confirm-workflow', {
    method: 'POST',
    data: proposal,
    ...(options || {}),
  });
}

export async function clearAdminMemory(options?: { [key: string]: any }) {
  return request('/api/clear-admin-memory', {
    method: 'POST',
    ...(options || {}),
  });
}

// Tools APIs
export async function getTools(options?: { [key: string]: any }) {
  return request<any[]>('/api/tools', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createTool(data: any, options?: { [key: string]: any }) {
  return request('/api/tools', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function updateTool(id: number, data: any, options?: { [key: string]: any }) {
  return request(`/api/tools/${id}`, {
    method: 'PATCH',
    data,
    ...(options || {}),
  });
}

export async function deleteTool(id: number, options?: { [key: string]: any }) {
  return request(`/api/tools/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function testTool(id: number, testData: any, options?: { [key: string]: any }) {
  return request(`/api/tools/${id}/test`, {
    method: 'POST',
    data: testData,
    ...(options || {}),
  });
}

// Rules APIs
export async function getRules(options?: { [key: string]: any }) {
  return request<any[]>('/api/rules', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createRule(data: any, options?: { [key: string]: any }) {
  return request('/api/rules', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function updateRule(id: number, data: any, options?: { [key: string]: any }) {
  return request(`/api/rules/${id}`, {
    method: 'PATCH',
    data,
    ...(options || {}),
  });
}

export async function deleteRule(id: number, options?: { [key: string]: any }) {
  return request(`/api/rules/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function testRule(text: string, options?: { [key: string]: any }) {
  return request('/api/test-rule', {
    method: 'POST',
    data: { text },
    ...(options || {}),
  });
}

// Config APIs
export async function getConfig(options?: { [key: string]: any }) {
  return request<any>('/api/config', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function saveConfig(data: any, options?: { [key: string]: any }) {
  return request('/api/config', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

// Voice APIs - ElevenLabs direct
export async function getVoices(options?: { [key: string]: any }) {
  return request<{ voices: any[] }>('/api/elevenlabs-voices', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getElevenLabsTTS(
  text: string,
  voiceId?: string,
  voiceSettings?: {
    model_id?: string;
    stability?: string;
    similarity?: string;
    style?: string;
    use_speaker_boost?: boolean;
  },
  options?: { [key: string]: any }
) {
  const params: any = { text };
  if (voiceId) params.voice_id = voiceId;
  if (voiceSettings) {
    if (voiceSettings.model_id) params.model_id = voiceSettings.model_id;
    if (voiceSettings.stability) params.stability = voiceSettings.stability;
    if (voiceSettings.similarity) params.similarity = voiceSettings.similarity;
    if (voiceSettings.style) params.style = voiceSettings.style;
    if (voiceSettings.use_speaker_boost !== undefined) params.use_speaker_boost = voiceSettings.use_speaker_boost;
  }
  return request('/api/elevenlabs-tts', {
    method: 'GET',
    params,
    responseType: 'blob',
    ...(options || {}),
  });
}

// Twilio APIs
export async function getTwilioStatus(options?: { [key: string]: any }) {
  return request<any>('/api/twilio-status', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function sendSmsTest(to: string, body: string, options?: { [key: string]: any }) {
  return request('/api/sms-test', {
    method: 'POST',
    data: { to, body },
    ...(options || {}),
  });
}

// Chat Channels APIs
export async function getChannels(options?: { [key: string]: any }) {
  return request<any[]>('/api/chat-channels', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createChannel(options?: { [key: string]: any }) {
  return request<any>('/api/chat-channels', {
    method: 'POST',
    ...(options || {}),
  });
}

export async function getChannel(channelId: number, options?: { [key: string]: any }) {
  return request<any>(`/api/chat-channels/${channelId}`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function updateChannel(channelId: number, name: string, options?: { [key: string]: any }) {
  return request<any>(`/api/chat-channels/${channelId}`, {
    method: 'PUT',
    data: { name },
    ...(options || {}),
  });
}

export async function deleteChannel(channelId: number, options?: { [key: string]: any }) {
  return request<any>(`/api/chat-channels/${channelId}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function getChannelMessages(channelId: number, options?: { [key: string]: any }) {
  return request<any[]>(`/api/chat-channels/${channelId}/messages`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function sendChannelMessage(channelId: number, message: string, options?: { [key: string]: any }) {
  return request<any>(`/api/chat-channels/${channelId}/messages`, {
    method: 'POST',
    data: { message },
    ...(options || {}),
  });
}

export async function clearChannelMessages(channelId: number, options?: { [key: string]: any }) {
  return request<any>(`/api/chat-channels/${channelId}/messages`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

// Agent Management APIs
export async function getAgents(agentType?: string, options?: { [key: string]: any }) {
  return request<{ agents: any[] }>('/api/agents', {
    method: 'GET',
    params: agentType ? { agent_type: agentType } : undefined,
    ...(options || {}),
  });
}

export async function getAgent(agentId: number, options?: { [key: string]: any }) {
  return request<any>(`/api/agents/${agentId}`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createAgent(data: any, options?: { [key: string]: any }) {
  return request('/api/agents', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function updateAgent(agentId: number, data: any, options?: { [key: string]: any }) {
  return request(`/api/agents/${agentId}`, {
    method: 'PATCH',
    data,
    ...(options || {}),
  });
}

export async function deleteAgent(agentId: number, options?: { [key: string]: any }) {
  return request(`/api/agents/${agentId}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function getAgentPromptVersions(
  agentId: number,
  channel: string,
  locale: string,
  options?: { [key: string]: any }
) {
  return request<{ versions: any[] }>(`/api/agents/${agentId}/prompt-versions`, {
    method: 'GET',
    params: { channel, locale },
    ...(options || {}),
  });
}

export async function getDefaultAgent(options?: { [key: string]: any }) {
  return request<any>('/api/agents/default', {
    method: 'GET',
    ...(options || {}),
  });
}

// Workflow Management APIs
export async function getWorkflows(options?: { [key: string]: any }) {
  return request<any[]>('/api/workflows', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createWorkflow(data: any, options?: { [key: string]: any }) {
  return request('/api/workflows', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function updateWorkflow(workflowId: number, data: any, options?: { [key: string]: any }) {
  return request(`/api/workflows/${workflowId}`, {
    method: 'PATCH',
    data,
    ...(options || {}),
  });
}

export async function deleteWorkflow(workflowId: number, options?: { [key: string]: any }) {
  return request(`/api/workflows/${workflowId}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function testWorkflow(id: number, testData: any, options?: { [key: string]: any }) {
  return request(`/api/workflows/${id}/test`, {
    method: 'POST',
    data: testData,
    ...(options || {}),
  });
}

// System Prompts APIs
export async function getSystemPrompts(options?: { [key: string]: any }) {
  return request<{ prompts: any[] }>('/api/system-prompts', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getSystemPromptById(promptId: number, options?: { [key: string]: any }) {
  return request<any>(`/api/system-prompts/${promptId}`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createSystemPrompt(data: any, options?: { [key: string]: any }) {
  return request('/api/system-prompts', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function updateSystemPrompt(promptId: number, data: any, options?: { [key: string]: any }) {
  return request(`/api/system-prompts/${promptId}`, {
    method: 'PATCH',
    data,
    ...(options || {}),
  });
}

export async function deleteSystemPrompt(promptId: number, options?: { [key: string]: any }) {
  return request(`/api/system-prompts/${promptId}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function updateSystemPromptVersionStatus(
  versionId: number,
  status: string,
  options?: { [key: string]: any }
) {
  return request(`/api/system-prompts/versions/${versionId}/status`, {
    method: 'PATCH',
    data: { status },
    ...(options || {}),
  });
}

// Voices APIs
export async function getVoiceConfigs(options?: { inbound_only?: boolean; [key: string]: any }) {
  const { inbound_only, ...rest } = options || {};
  const params: Record<string, any> = {};
  if (inbound_only) {
    params.inbound_only = true;
  }
  return request<{ voices: any[] }>('/api/voices', {
    method: 'GET',
    params,
    ...rest,
  });
}

export async function getVoiceConfig(voiceId: number, options?: { [key: string]: any }) {
  return request<any>(`/api/voices/${voiceId}`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createVoiceConfig(data: any, options?: { [key: string]: any }) {
  return request('/api/voices', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function updateVoiceConfig(voiceId: number, data: any, options?: { [key: string]: any }) {
  return request(`/api/voices/${voiceId}`, {
    method: 'PATCH',
    data,
    ...(options || {}),
  });
}

export async function deleteVoiceConfig(voiceId: number, options?: { [key: string]: any }) {
  return request(`/api/voices/${voiceId}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

// Twilio Config APIs
export async function getTwilioConfigs(options?: { [key: string]: any }) {
  return request<{ configs: any[] }>('/api/twilio-configs', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getTwilioConfig(configId: number, options?: { [key: string]: any }) {
  return request<any>(`/api/twilio-configs/${configId}`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createTwilioConfig(data: any, options?: { [key: string]: any }) {
  return request('/api/twilio-configs', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function updateTwilioConfig(configId: number, data: any, options?: { [key: string]: any }) {
  return request(`/api/twilio-configs/${configId}`, {
    method: 'PATCH',
    data,
    ...(options || {}),
  });
}

export async function deleteTwilioConfig(configId: number, options?: { [key: string]: any }) {
  return request(`/api/twilio-configs/${configId}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

// Human Assistant Management APIs
export async function getHumanAssistants(params?: { sync?: boolean }, options?: { [key: string]: any }) {
  return request<{ assistants: API.HumanAssistant[] }>('/api/human-assistants', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function getExternalHumanAssistants(params?: any, options?: { [key: string]: any }) {
  return request<{ success: boolean; data: API.ExternalEmployeeResponse }>('/api/human-assistants/external', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function createHumanAssistant(data: any, options?: { [key: string]: any }) {
  return request('/api/human-assistants', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function updateHumanAssistant(assistantId: number, data: any, options?: { [key: string]: any }) {
  return request(`/api/human-assistants/${assistantId}`, {
    method: 'PUT',
    data,
    ...(options || {}),
  });
}

export async function deleteHumanAssistant(assistantId: number, options?: { [key: string]: any }) {
  return request(`/api/human-assistants/${assistantId}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

// Dispatch Butler Roles APIs
export async function getDispatchButlerRoles(options?: { [key: string]: any }) {
  return request<API.DispatchButlerRolesResponse>('/api/dispatch-butler/roles', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getDispatchButlerRole(
  roleType: 'tenant' | 'employee',
  roleId: number,
  options?: { [key: string]: any },
) {
  return request<{ success: boolean; data: { role: API.DispatchButlerRole } }>(
    `/api/dispatch-butler/roles/${roleType}/${roleId}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

export async function getDispatchButlerRoleUsers(
  roleType: 'tenant' | 'employee',
  roleId: number,
  params?: { current?: number; pageSize?: number },
  options?: { [key: string]: any },
) {
  return request<API.DispatchButlerRoleUsersResponse>(
    `/api/dispatch-butler/roles/${roleType}/${roleId}/users`,
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
}

export async function getDispatchButlerCalls(
  params?: {
    start_date?: string;
    end_date?: string;
    from_number?: string;
    to_number?: string;
    status?: string;
    direction?: string;
    user_id?: number;
    role_type?: 'tenant' | 'employee';
    role_id?: number;
    order?: 'asc' | 'desc';
    current?: number;
    pageSize?: number;
  },
  options?: { [key: string]: any },
) {
  return request<API.DispatchButlerCallsResponse>('/api/dispatch-butler/calls', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function getDispatchButlerCall(
  callId: number,
  options?: { [key: string]: any },
) {
  return request<API.DispatchButlerCallDetailResponse>(`/api/dispatch-butler/calls/${callId}`, {
    method: 'GET',
    ...(options || {}),
  });
}

export function getDispatchButlerCallRecordingUrl(callId: number, download = false): string {
  return download
    ? `/api/dispatch-butler/calls/${callId}/recording?download=true`
    : `/api/dispatch-butler/calls/${callId}/recording`;
}

export async function getTransferSettings(options?: { [key: string]: any }) {
  return request<any>('/api/transfer-settings', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function updateTransferSettings(data: any, options?: { [key: string]: any }) {
  return request('/api/transfer-settings', {
    method: 'PUT',
    data,
    ...(options || {}),
  });
}

export async function getCallQueue(params?: any, options?: { [key: string]: any }) {
  return request<{ queue: any[]; totalDocs?: number; currentPage?: number; pageSize?: number }>('/api/call-queue', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function updateCallQueue(queueId: number, data: any, options?: { [key: string]: any }) {
  return request(`/api/call-queue/${queueId}`, {
    method: 'PUT',
    data,
    ...(options || {}),
  });
}

export async function deleteCallQueue(queueId: number, options?: { [key: string]: any }) {
  return request(`/api/call-queue/${queueId}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

// ===================== RingCentral Calls =====================
export async function getRingCentralCalls(params?: any, options?: { [key: string]: any }) {
  return request<{ data: API.RingCentralCall[]; total: number; success: boolean }>(
    '/api/ringcentral/calls',
    { method: 'GET', params, ...(options || {}) },
  );
}

export async function getRingCentralCallDetail(id: number, options?: { [key: string]: any }) {
  return request<{ data: API.RingCentralCallDetail; success: boolean }>(
    `/api/ringcentral/calls/${id}`,
    { method: 'GET', ...(options || {}) },
  );
}

export async function getRingCentralStats(params?: any, options?: { [key: string]: any }) {
  return request<{ data: API.RingCentralStats; success: boolean }>(
    '/api/ringcentral/stats',
    { method: 'GET', params, ...(options || {}) },
  );
}

export async function getRingCentralMessages(params?: any, options?: { [key: string]: any }) {
  return request<{ data: any[]; total: number; success: boolean }>(
    '/api/ringcentral/messages',
    { method: 'GET', params, ...(options || {}) },
  );
}

export async function triggerRingCentralSync(options?: { [key: string]: any }) {
  return request<{ success: boolean; inserted: number; seen: number }>(
    '/api/ringcentral/sync',
    { method: 'POST', ...(options || {}) },
  );
}
